import React from "react"
import { Card, CardBody, CardHeader } from "react-bootstrap"
import { Flex, Button } from "@inmagik/magik-ui"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { optionsBlockTypes } from "../options"
import type { BlockBase } from "../../../types"
import ModalDelete from "../../../components/ModalDelete"
import useModalTrigger from "magik-react-hooks/useModalTrigger"

export function SortableBlockCard({
  block,
  orderIndex,
  onEdit,
  onDelete,
  expanded,
  onToggle,
  collapsedHint,
  preview,
}: {
  block: BlockBase
  orderIndex: number
  onEdit: (b: BlockBase) => void
  onDelete: (uuid: string) => void
  expanded: boolean
  onToggle: () => void
  collapsedHint: React.ReactNode
  preview: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.uuid })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  const typeLabel = optionsBlockTypes.find((o) => o.value === block.blockType)?.label || "Tipo non selezionato"
  const headerId = `block-header-${block.uuid}`
  const bodyId = `block-body-${block.uuid}`

  const [modalStateDeleteBlock, modalActionsDeleteBlock] = useModalTrigger()

  return (
    <div ref={setNodeRef} style={style}>
      <Card>
        <CardHeader
          id={headerId}
          className="d-flex justify-content-between align-items-center"
          role="button"
          tabIndex={0}
          aria-controls={bodyId}
          aria-expanded={expanded}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onToggle()
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              {...attributes}
              {...listeners}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              title="Trascina per riordinare"
              style={{
                cursor: "grab",
                userSelect: "none",
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid rgba(0,0,0,.1)",
              }}
              aria-label="Trascina per riordinare"
            >
              ⠿
            </span>
            {/* Numero ordine */}
            <span
              style={{
                minWidth: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "#0d6efd",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 600,
              }}
              title={`Posizione ${orderIndex}`}
              aria-label={`Posizione ${orderIndex}`}
            >
              {orderIndex}
            </span>

            <strong>{typeLabel}</strong>
          </div>

          {/* Azioni (non devono togglare) */}
          <Flex
            gap={4}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <Button size="sm" title="" onClick={() => onEdit(block)} leadingIcon="edit" />
            <Button size="sm" variant="danger" title="" onClick={modalActionsDeleteBlock.open} leadingIcon="delete" />
          </Flex>
        </CardHeader>

        {!expanded && <CardBody className="py-2">{collapsedHint}</CardBody>}
        {expanded && <CardBody id={bodyId}>{preview}</CardBody>}
      </Card>
      <ModalDelete
        title="Elimina Blocco"
        message="Sei sicuro di voler eliminare questo blocco? Questa azione non è reversibile."
        onDelete={() => onDelete(block.uuid)}
        isOpen={modalStateDeleteBlock.isOpen}
        onClose={modalActionsDeleteBlock.close}
      />
    </div>
  )
}
